import {GetProductBulkResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {StarRating, Text} from '@aboutyou/ui-components';
import React, {FC} from 'react';
import styled from 'styled-components';
import {ReviewsActionStatement} from './ReviewsActionStatement';
import {ReviewsUserReview} from './ReviewsUserReview';
import {starRatingMap} from './starRatingMap';

export const ReviewsSectionStream: FC<
  GetProductBulkResponse['reviewSection'] & {
    enableZeroOverallRatings?: boolean;
    disableActionStatements?: boolean;
    disableUserReviews?: boolean;
  }
> = ({
  summary,
  enableZeroOverallRatings,
  disableActionStatements,
  disableUserReviews,
}) => {
  const {t} = useTranslation();

  if (!summary && enableZeroOverallRatings) {
    return (
      <OverallRating
        starRatingNormalized={0}
        quantityReviewLabel={t('{ratingsQuantity} ratings', {
          ratingsQuantity: 0,
        })}
      />
    );
  }

  if (!summary) return null;

  const {
    overallRating,
    overallStarRating,
    actionStatements,
    showcaseReviews,
    reviewCount,
  } = summary;

  const quantityReviewLabel =
    reviewCount === 1
      ? t('{ratingsQuantity} rating', {ratingsQuantity: reviewCount})
      : t('{ratingsQuantity} ratings', {ratingsQuantity: reviewCount});

  return (
    <>
      <OverallRating
        starRatingNormalized={starRatingMap[overallStarRating]}
        overallRating={overallRating}
        quantityReviewLabel={quantityReviewLabel}
      />
      {!disableActionStatements && (
        <ReviewsActionStatement actionStatements={actionStatements} />
      )}
      {!disableUserReviews && (
        <ReviewsUserReview showcaseReviews={showcaseReviews} />
      )}
    </>
  );
};

const OverallRating: FC<{
  starRatingNormalized: number;
  overallRating?: number;
  quantityReviewLabel: string;
}> = ({starRatingNormalized, overallRating, quantityReviewLabel}) => {
  return (
    <OverallRatingWrapper data-testid="overallStarsRatings">
      <OverallRatingContainer>
        <OverallStarsContainer>
          <StarRating
            value={starRatingNormalized}
            size="L"
            dataTestid={`overallStarsRating-${starRatingNormalized}`}
            outlineColor={starRatingNormalized > 0 ? 'Black' : 'Grey1'}
          />
          {overallRating && (
            <Text data-testid="overallRating">{overallRating}</Text>
          )}
        </OverallStarsContainer>
        <Text color="Grey1" data-testid="quantityRatingsLabel">
          {quantityReviewLabel}
        </Text>
      </OverallRatingContainer>
    </OverallRatingWrapper>
  );
};

const OverallRatingWrapper = styled.div`
  display: flex;
  margin-bottom: 20px;
`;

const OverallRatingContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const OverallStarsContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
`;
